import Entity from "_framework/entities/entity";
import KnowledgeBaseArticleSchema, {
  ArticleOrigin,
  KnowledgeBaseLibrary,
  ArticlePublication,
  ArticleTopicJSON,
  KnowledgeBaseArticleJSON,
  KnowledgeBaseArticleParams,
} from "knowledge-base/_schemas/knowledge-base-article-schema";
import {
  articlePathIn,
  attorneyHubKnowledgeBaseRoot,
  entityKnowledgeBaseRoot,
} from "knowledge-base/knowledge-base-locations";

/**
 * One page of the knowledge base.
 *
 * The browser never decides who may read an article — the server filters that before responding —
 * so nothing here is an authorization check. What this class does hold is the difference between an
 * ordinary page and one that is also the text of an outbound letter, which changes how the editor
 * has to behave.
 */
export default class KnowledgeBaseArticle extends Entity<
  KnowledgeBaseArticle,
  KnowledgeBaseArticleJSON,
  KnowledgeBaseArticleParams
> {
  public static create(params: KnowledgeBaseArticleParams): KnowledgeBaseArticle {
    return new KnowledgeBaseArticle(KnowledgeBaseArticleSchema.validate(params));
  }

  public static fromJSON(json: KnowledgeBaseArticleJSON): KnowledgeBaseArticle {
    return new KnowledgeBaseArticle(KnowledgeBaseArticleSchema.deserialize(json));
  }

  public static fromJSONArray(json: KnowledgeBaseArticleJSON[]): KnowledgeBaseArticle[] {
    return json.map((article) => KnowledgeBaseArticle.fromJSON(article));
  }

  public readonly id: string;
  public readonly slug: string;
  public readonly title: string;
  public readonly body: string;
  public readonly library: KnowledgeBaseLibrary;
  public readonly publication: ArticlePublication;
  public readonly categoryId?: string;
  public readonly position: number;
  public readonly templateKey?: string;
  public readonly templateSubject?: string;
  public readonly topics: ArticleTopicJSON[];
  public readonly attachedDocumentIds: string[];
  public readonly origin: ArticleOrigin;
  public readonly sourceDocumentId?: string;
  public readonly summarizedBy?: string;
  public readonly summarizedAt?: Date;
  public readonly created?: Date;
  public readonly updated?: Date;
  public readonly published?: Date;
  public readonly currentRevisionNumber?: number;

  /**
   * Whether this article is the live source of a letter the application sends.
   *
   * Editing one of these changes what a law firm receives, so the editor shows the placeholders the
   * letter is allowed to use and the subject line alongside the body.
   */
  public get isFormLetter(): boolean {
    return Boolean(this.templateKey);
  }

  /**
   * Whether an agent wrote this text from an uploaded document rather than a person writing it.
   *
   * Worth showing prominently: a summary of a compliance policy is a claim about that policy, and
   * a reader deciding what they are required to do should know they are reading a précis and where
   * the original is.
   */
  public get isAgentSummary(): boolean {
    return this.origin === "AgentSummarized";
  }

  public get isPublished(): boolean {
    return this.publication === "Published";
  }

  public get isDraft(): boolean {
    return this.publication === "Draft";
  }

  public get isArchived(): boolean {
    return this.publication === "Archived";
  }

  /**
   * Where this article is read.
   *
   * Which knowledge base it belongs to decides the address, not only the slug: Attorney Hub's
   * public library has its own, and linking one of its articles under the entity route opens a
   * different knowledge base altogether.
   */
  public get path(): string {
    return articlePathIn(
      this.library === "AttorneyHubPublic"
        ? attorneyHubKnowledgeBaseRoot
        : entityKnowledgeBaseRoot,
      this.slug
    );
  }

  public constructor(params: KnowledgeBaseArticleParams) {
    super(params);

    this.id = params.id;
    this.slug = params.slug;
    this.title = params.title;
    this.body = params.body;
    this.library = params.library;
    this.publication = params.publication;
    this.categoryId = params.categoryId;
    this.position = params.position;
    this.templateKey = params.templateKey;
    this.templateSubject = params.templateSubject;
    this.topics = params.topics;
    this.attachedDocumentIds = params.attachedDocumentIds;
    this.origin = params.origin;
    this.sourceDocumentId = params.sourceDocumentId;
    this.summarizedBy = params.summarizedBy;
    this.summarizedAt = params.summarizedAt;
    this.created = params.created;
    this.updated = params.updated;
    this.published = params.published;
    this.currentRevisionNumber = params.currentRevisionNumber;
  }

  public toJSON(): KnowledgeBaseArticleJSON {
    return KnowledgeBaseArticleSchema.serialize(this);
  }

  public valueOf(): KnowledgeBaseArticleJSON {
    return this.toJSON();
  }

  public toString(): string {
    return `${this.title} (${this.slug})`;
  }

  public isEqualTo(other: KnowledgeBaseArticle | null | undefined): boolean {
    if (!other) return false;

    return (
      this.id === other.id &&
      this.slug === other.slug &&
      this.title === other.title &&
      this.body === other.body &&
      this.library === other.library &&
      this.publication === other.publication &&
      this.templateSubject === other.templateSubject &&
      this.currentRevisionNumber === other.currentRevisionNumber
    );
  }
}
